/**
 * PageRenderer
 *
 * Takes a PageConfig (list of blocks + settings) and renders them
 * using the component registry. Used by both the admin preview
 * and the public-facing detail pages.
 *
 * Supports two layout modes via settings.layout:
 *   'default'   — single column (default)
 *   'data-room' — two-column grid: sticky 300px sidebar + scrollable main
 *                 Blocks tagged layoutHint='sidebar' go in the right column.
 *                 Blocks tagged layoutHint='full' span the full width above the grid.
 *                 All other blocks go in the left main column.
 */
import React, { Suspense } from 'react';
import type { BlockInstance, EntityContext, PageConfig } from './types';
import { getBlockComponent } from './componentRegistry';

interface PageRendererProps {
  config: PageConfig;
  entity?: EntityContext;
  isEditing?: boolean;
}

const MAX_WIDTH_MAP: Record<string, string> = {
  sm: 'max-w-3xl',
  md: 'max-w-5xl',
  lg: 'max-w-6xl',
  xl: 'max-w-7xl',
  full: 'max-w-full',
};

const SPACING_MAP: Record<string, string> = {
  compact: 'space-y-4',
  normal: 'space-y-6',
  relaxed: 'space-y-10',
};

export function BlockWrapper({
  block,
  entity,
  isEditing,
}: {
  block: BlockInstance;
  entity?: EntityContext;
  isEditing?: boolean;
}) {
  const Component = getBlockComponent(block.blockKey);
  if (!Component) {
    if (isEditing) {
      return (
        <div className="p-4 rounded-lg border border-dashed border-red-300 bg-red-50 text-sm text-red-600">
          Unknown block type: <strong>{block.blockKey}</strong>
        </div>
      );
    }
    return null;
  }

  return (
    <Suspense fallback={<div className="h-24 animate-pulse bg-muted rounded-lg" />}>
      <Component config={block.props} entity={entity} isEditing={isEditing} />
    </Suspense>
  );
}

export function PageRenderer({ config, entity, isEditing }: PageRendererProps) {
  const { blocks, settings } = config;
  const visibleBlocks = blocks
    .filter(b => b.visible)
    .sort((a, b) => a.order - b.order);

  const maxWidth = MAX_WIDTH_MAP[settings?.maxWidth || 'xl'] || MAX_WIDTH_MAP.xl;
  const spacing = SPACING_MAP[settings?.spacing || 'normal'] || SPACING_MAP.normal;

  // ── Data-Room layout: full-width header rows + 2-col grid ──────────
  if (settings?.layout === 'data-room') {
    const fullBlocks    = visibleBlocks.filter(b => b.layoutHint === 'full');
    const sidebarBlocks = visibleBlocks.filter(b => b.layoutHint === 'sidebar');
    const mainBlocks    = visibleBlocks.filter(b => !b.layoutHint || b.layoutHint === 'main');

    return (
      <div
        className={`mx-auto px-4 md:px-6 pb-12 ${maxWidth}`}
        style={settings?.background ? { background: settings.background } : undefined}
      >
        {/* Full-width blocks (hero, etc.) */}
        {fullBlocks.length > 0 && (
          <div className={`${spacing} mb-8`}>
            {fullBlocks.map(block => (
              <BlockWrapper key={block.id} block={block} entity={entity} isEditing={isEditing} />
            ))}
          </div>
        )}

        {/* Two-column body */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-10 items-start">
          {/* Left: main scrollable content */}
          <div className={spacing}>
            {mainBlocks.map(block => (
              <BlockWrapper key={block.id} block={block} entity={entity} isEditing={isEditing} />
            ))}
          </div>

          {/* Right: sticky sidebar */}
          <div className="space-y-4 lg:sticky lg:top-6">
            {sidebarBlocks.map(block => (
              <BlockWrapper key={block.id} block={block} entity={entity} isEditing={isEditing} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Default: single-column ─────────────────────────────────────────
  return (
    <div
      className={`mx-auto px-4 md:px-6 ${maxWidth} ${spacing}`}
      style={settings?.background ? { background: settings.background } : undefined}
    >
      {visibleBlocks.map((block) => (
        <BlockWrapper key={block.id} block={block} entity={entity} isEditing={isEditing} />
      ))}
    </div>
  );
}
